import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, ArrowLeftRight, Gamepad2, Trophy, Pencil, Trash2, Plus, ArrowLeft, Flame, Calendar, MapPin, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, adminHeaders, getToken, setToken } from "@/lib/api";
import { formatDateShort, isConcluded } from "@/lib/format";
import { BottomTab } from "@/components/BottomTab";
import { warmupFrom } from "@/lib/warmup";
import { PositionLock } from "@/components/PositionLock";

function Login({ onOk }) {
  const nav = useNavigate();
  const [tok, setTok] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/verify", {}, { headers: { "X-Admin-Token": tok } });
      setToken(tok);
      onOk();
    } catch {
      toast.error("Token non valido");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-surface flex flex-col px-8 pb-24 relative" data-testid="admin-login">
      <button data-testid="admin-login-back" onClick={() => nav("/")} className="absolute left-5 text-onsurface" style={{ top: "calc(var(--safe-top) + 20px)" }}>
        <ArrowLeft size={30} strokeWidth={2.5} />
      </button>
      <div className="flex-1 flex flex-col items-center justify-center">
      <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center mb-6">
        <Lock className="text-brand" size={34} />
      </div>
      <h1 className="text-3xl font-black text-onsurface">Only Staff</h1>
      <p className="text-onsurface3 text-center mt-2 mb-8 font-secondary">Questa sezione è accessibile solo allo staff</p>
      <form onSubmit={submit} className="w-full max-w-sm">
        <input
          data-testid="admin-token-input"
          type="password"
          value={tok}
          onChange={(e) => setTok(e.target.value)}
          placeholder="Inserisci token"
          className="w-full bg-surface2 rounded-2xl px-5 py-4 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-center"
        />
        <button data-testid="admin-login-btn" disabled={busy} className="w-full mt-3 bg-brand text-onbrand rounded-2xl py-4 font-black text-lg disabled:opacity-60">
          {busy ? "..." : "Accedi"}
        </button>
      </form>
      </div>
      <BottomTab />
    </div>
  );
}

function EventPicker({ event, onPick }) {
  const concluded = event && isConcluded(event.date_iso);
  const [unlocked, setUnlocked] = useState(false);
  const locked = concluded && !unlocked;

  const handle = () => {
    if (locked) {
      const ok = window.confirm("Evento concluso e protetto. Vuoi sbloccarlo per apportare modifiche?");
      if (!ok) return;
      setUnlocked(true);
      return;
    }
    onPick();
  };

  return (
    <div className="px-5 pt-6" data-testid="admin-event-picker">
      <h2 className="font-black text-3xl text-onsurface">Seleziona un evento</h2>
      <p className="text-onsurface3 mt-2 mb-5 leading-snug">Scegli l'evento che vuoi gestire. Gli eventi conclusi sono protetti da un lucchetto.</p>

      <div className="bg-surface2 rounded-3xl p-6">
        <div className="mb-3">
          {locked ? (
            <span className="inline-flex items-center gap-2 bg-surface3 text-onsurface rounded-full px-4 py-1.5 text-sm font-black tracking-wide">
              <Lock size={16} /> CONCLUSO
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 bg-brand text-onbrand rounded-full px-4 py-1.5 text-sm font-black tracking-wide">
              <Flame size={16} /> {concluded ? "SBLOCCATO" : "ATTIVO"}
            </span>
          )}
        </div>
        <p className="text-2xl font-black text-onsurface">{event?.name}</p>
        <p className="text-brand font-black tracking-widest text-sm mt-0.5">{(event?.subtitle || "").toUpperCase()}</p>
        <div className="mt-4 flex items-center gap-2 text-onsurface flex-wrap">
          <Calendar className="text-onsurface3" size={18} />
          <span className="font-bold">{event ? formatDateShort(event.date_iso) : ""}</span>
          <span className="text-onsurface3 mx-1">·</span>
          <MapPin className="text-onsurface3" size={18} />
        </div>
        <p className="text-onsurface font-bold mt-1">{event?.location_name}</p>
        <div className="h-px bg-surface3 my-4" />
        <button onClick={handle} data-testid="admin-pick-event" className="w-full flex items-center justify-center gap-2 text-brand font-black text-lg">
          {locked ? <>Sblocca e gestisci <Lock size={18} /></> : <>Gestisci evento <ArrowRight size={20} strokeWidth={3} /></>}
        </button>
      </div>
    </div>
  );
}

function AthleteForm({ onCreated }) {
  const empty = { name: "", gender: "Men", division: "Red", instagram: "", race_time: "", lane: "", rounds: "", reps: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Inserisci il nome"); return; }
    const rounds = f.rounds.trim() ? parseInt(f.rounds, 10) : null;
    if (f.rounds.trim() && Number.isNaN(rounds)) { toast.error("Giri non valido"); return; }
    const reps = f.reps.trim() ? parseInt(f.reps, 10) : 0;
    setBusy(true);
    try {
      await api.post("/athletes", {
        name: f.name, gender: f.gender, division: f.gender === "Women" ? "Open" : f.division, instagram: f.instagram,
        race_time: f.race_time, lane: f.lane,
        workout_rounds: rounds, workout_reps: rounds != null ? reps : null,
      }, { headers: adminHeaders() });
      toast.success("Atleta aggiunto");
      setF(empty);
      onCreated();
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  const Toggle = ({ field, options }) => (
    <div className="flex gap-2 bg-surface p-1 rounded-xl">
      {options.map(([v, l, active]) => (
        <button key={v} type="button" onClick={() => setF({ ...f, [field]: v })}
          className={`flex-1 py-2 rounded-lg font-bold text-sm ${f[field] === v ? active : "text-onsurface3"}`}>
          {l}
        </button>
      ))}
    </div>
  );

  const input = "w-full bg-surface rounded-xl px-4 py-3 font-bold text-onsurface outline-none focus:ring-2 ring-brand";

  return (
    <form onSubmit={submit} className="bg-surface2 rounded-2xl p-4 space-y-3" data-testid="add-athlete-form">
      <h3 className="font-black text-lg text-onsurface flex items-center gap-2"><Plus size={20} /> Aggiungi atleta</h3>
      <input data-testid="athlete-name-input" className={input} placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Toggle field="gender" options={[["Men", "Uomo", "bg-brand text-onbrand"], ["Women", "Donna", "bg-brand text-onbrand"]]} />
      {f.gender === "Men" && (
        <Toggle field="division" options={[["Red", "Red", "bg-mberror text-white"], ["Black", "Black", "bg-black text-white"]]} />
      )}
      <input data-testid="athlete-ig-input" className={input} placeholder="Instagram (senza @)" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="athlete-race-input" className={input} placeholder="Ingresso gara (HH:MM)" value={f.race_time} onChange={(e) => setF({ ...f, race_time: e.target.value })} />
        <div className="bg-surface rounded-xl px-4 py-3 font-bold text-onsurface3 text-sm flex items-center" data-testid="athlete-warmup-auto">Warm up {warmupFrom(f.race_time) || "—"}</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <input className={input} placeholder="Lane" value={f.lane} onChange={(e) => setF({ ...f, lane: e.target.value })} />
        <input data-testid="athlete-rounds-input" className={input} type="number" inputMode="numeric" placeholder="Giri" value={f.rounds} onChange={(e) => setF({ ...f, rounds: e.target.value })} />
        <input data-testid="athlete-reps-input" className={input} type="number" inputMode="numeric" placeholder="Reps" value={f.reps} onChange={(e) => setF({ ...f, reps: e.target.value })} />
      </div>
      <button data-testid="athlete-save-btn" disabled={busy} className="w-full bg-brand text-onbrand rounded-xl py-3 font-black disabled:opacity-60">
        {busy ? "..." : "Aggiungi atleta"}
      </button>
    </form>
  );
}

function SplitConfig({ gender }) {
  const rows = gender === "Women"
    ? [["Women", "Cuori", "Fiori"]]
    : [["Red", "Cuori", "Quadri"], ["Black", "Fiori", "Picche"]];
  const [wr, setWr] = useState(null);
  const [cfg, setCfg] = useState({});
  const [vals, setVals] = useState({});

  const load = async () => {
    const [w, s] = await Promise.all([
      api.get(`/workout-ranking?gender=${gender}`),
      api.get(`/split-config?gender=${gender}`),
    ]);
    setWr(w.data);
    const c = {};
    (s.data || []).forEach((d) => { c[d.division] = d.top_count; });
    setCfg(c);
    setVals({});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gender]);

  const timedFor = (div) => {
    if (!wr) return 0;
    if (wr.single) return wr.total_timed;
    return div === "Red" ? wr.red.total_timed : wr.black.total_timed;
  };

  const save = async (div, top) => {
    await api.post("/split-config", { gender, division: div, top_count: top }, { headers: adminHeaders() });
    toast.success(top === null ? "Tornato automatico" : "Divisione aggiornata");
    load();
  };

  return (
    <div className="bg-surface2 rounded-2xl p-4 space-y-3" data-testid="split-config">
      <p className="font-black text-onsurface text-sm tracking-wide">DIVISIONE SEMI (manuale)</p>
      {rows.map(([div, top, bottom]) => {
        const n = timedFor(div);
        const auto = Math.floor(n / 2);
        const current = cfg[div] != null ? cfg[div] : auto;
        const val = vals[div] != null ? vals[div] : String(current);
        const topN = Math.max(0, Math.min(parseInt(val || "0", 10) || 0, n));
        return (
          <div key={div} className="border-t border-surface3 pt-3 first:border-0 first:pt-0">
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-onsurface text-sm">{gender === "Women" ? "Donne" : div === "Red" ? "RED" : "BLACK"}</span>
              <span className="text-[12px] text-onsurface3">{n} classificati · {cfg[div] != null ? "manuale" : "auto"}</span>
            </div>
            <div className="flex items-center gap-2">
              <input data-testid={`split-input-${div}`} type="number" inputMode="numeric"
                className="w-16 bg-surface rounded-lg px-2 py-2 font-bold text-onsurface text-sm outline-none focus:ring-2 ring-brand"
                value={val} onChange={(e) => setVals({ ...vals, [div]: e.target.value })} />
              <span className="text-[12px] text-onsurface3 flex-1">{topN} {top} / {n - topN} {bottom}</span>
              <button data-testid={`split-save-${div}`} onClick={() => save(div, topN)} className="bg-brand text-onbrand rounded-lg px-3 py-2 font-black text-xs">Salva</button>
              <button data-testid={`split-auto-${div}`} onClick={() => save(div, null)} className="bg-black text-white rounded-lg px-3 py-2 font-black text-xs">Auto</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RosterRow({ a, rank, onChanged }) {
  const [open, setOpen] = useState(false);
  const [posLocked, setPosLocked] = useState(!!a.workout_position);
  const [pos, setPos] = useState(a.workout_position ? String(a.workout_position) : rank ? String(rank) : "");
  const [e, setE] = useState({
    race_time: a.race_time || "", lane: a.lane || "",
    rounds: a.workout_rounds != null ? String(a.workout_rounds) : "",
    reps: a.workout_reps != null ? String(a.workout_reps) : "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const rounds = e.rounds.trim() ? parseInt(e.rounds, 10) : null;
    if (e.rounds.trim() && Number.isNaN(rounds)) { toast.error("Giri non valido"); return; }
    const reps = e.reps.trim() ? parseInt(e.reps, 10) : 0;
    const position = posLocked ? parseInt(pos, 10) : null;
    if (posLocked && !(position > 0)) { toast.error("Posizione non valida"); return; }
    setBusy(true);
    try {
      await api.patch(`/athletes/${a.id}`, {
        workout_position: position,
        race_time: e.race_time, lane: e.lane,
        workout_rounds: rounds, workout_reps: rounds != null ? reps : null,
      }, { headers: adminHeaders() });
      toast.success("Salvato");
      setOpen(false);
      onChanged();
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Eliminare ${a.name}?`)) return;
    try {
      await api.delete(`/athletes/${a.id}`, { headers: adminHeaders() });
      toast.success("Eliminato");
      onChanged();
    } catch { toast.error("Errore"); }
  };

  const input = "w-full bg-surface rounded-xl px-3 py-2 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm";

  return (
    <div className="border-b border-surface3 last:border-0" data-testid={`roster-row-${a.id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-14 text-onsurface font-black tabular-nums text-sm" data-testid={`roster-rank-${a.id}`}>
          {rank ? <span className="flex items-center gap-1">{rank}°{a.workout_position ? <Lock size={12} className="text-brand" /> : null}</span> : null}
          <span className="text-[11px] text-onsurface3 font-bold">{a.workout_rounds != null ? `${a.workout_rounds}+${a.workout_reps || 0}` : "—"}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-onsurface break-words">{a.name}</p>
          <p className="text-[12px] text-onsurface3">Warm up {a.warmup_time || "—"} · Gara {a.race_time || "—"} · Lane {a.lane || "—"}</p>
        </div>
        <button data-testid={`roster-edit-${a.id}`} onClick={() => setOpen(!open)} className="text-onsurface p-2"><Pencil size={18} /></button>
        <button data-testid={`roster-delete-${a.id}`} onClick={del} className="text-mberror p-2"><Trash2 size={18} /></button>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-surface rounded-xl px-3 py-2 font-bold text-onsurface3 text-sm" data-testid={`roster-warmup-auto-${a.id}`}>WU {warmupFrom(e.race_time) || "—"}</div>
            <input data-testid={`roster-race-${a.id}`} className={input} placeholder="Gara" value={e.race_time} onChange={(ev) => setE({ ...e, race_time: ev.target.value })} />
            <input className={input} placeholder="Lane" value={e.lane} onChange={(ev) => setE({ ...e, lane: ev.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-onsurface3 flex-1">Posizione Ranking Workout {posLocked ? "(manuale)" : "(auto)"}</span>
            <PositionLock testid={`roster-${a.id}`} locked={posLocked} value={pos}
              onToggle={() => setPosLocked(!posLocked)} onChange={setPos} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`roster-rounds-${a.id}`} className={input} type="number" inputMode="numeric" placeholder="Giri" value={e.rounds} onChange={(ev) => setE({ ...e, rounds: ev.target.value })} />
            <input data-testid={`roster-reps-${a.id}`} className={input} type="number" inputMode="numeric" placeholder="Reps" value={e.reps} onChange={(ev) => setE({ ...e, reps: ev.target.value })} />
          </div>
          <button data-testid={`roster-save-${a.id}`} onClick={save} disabled={busy} className="w-full bg-brand text-onbrand rounded-xl py-2.5 font-black text-sm disabled:opacity-60">{busy ? "..." : "Salva modifiche"}</button>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(!!getToken());
  const [event, setEvent] = useState(null);
  const [picked, setPicked] = useState(false);
  const [gender, setGender] = useState("Men");
  const [athletes, setAthletes] = useState([]);
  const [ranks, setRanks] = useState({});

  const loadRoster = useCallback(() => {
    api.get(`/athletes?gender=${gender}`).then((r) => setAthletes(r.data)).catch(() => {});
    api.get(`/workout-ranking?gender=${gender}`).then((r) => {
      const rows = r.data.single ? r.data.rows : [...r.data.red.rows, ...r.data.black.rows];
      const m = {};
      rows.forEach((x) => { m[x.athlete_id] = x.rank; });
      setRanks(m);
    }).catch(() => {});
  }, [gender]);

  useEffect(() => { if (authed) api.get("/event").then((r) => setEvent(r.data)).catch(() => {}); }, [authed]);
  useEffect(() => { if (picked) loadRoster(); }, [picked, loadRoster]);

  if (!authed) return <><Toaster position="top-center" richColors /><Login onOk={() => setAuthed(true)} /></>;

  const logout = () => { setToken(""); setAuthed(false); nav("/"); };

  if (!picked) {
    return (
      <div className="min-h-screen bg-surface pb-24" data-testid="admin-page">
        <Toaster position="top-center" richColors />
        <header className="bg-black px-5 flex items-center justify-between pb-5" style={{ paddingTop: "calc(var(--safe-top) + 20px)" }}>
          <button data-testid="admin-back-btn" onClick={() => nav("/")} className="text-white"><ArrowLeft size={28} strokeWidth={2.5} /></button>
          <h1 className="text-2xl font-black text-white">Admin</h1>
          <button data-testid="admin-logout-btn" onClick={logout} className="text-brand"><LogOut size={24} /></button>
        </header>
        <EventPicker event={event} onPick={() => setPicked(true)} />
        <BottomTab />
      </div>
    );
  }

  const groups = gender === "Women"
    ? [["*", "ATLETE", "bg-black"]]
    : [["Red", "RED", "bg-mberror"], ["Black", "BLACK", "bg-black"]];

  return (
    <div className="min-h-screen bg-surface pb-24" data-testid="admin-tools">
      <Toaster position="top-center" richColors />
      <header className="bg-black px-5 flex items-center justify-between pb-4" style={{ paddingTop: "calc(var(--safe-top) + 20px)" }}>
        <button data-testid="admin-back-btn" onClick={() => setPicked(false)} className="text-white"><ArrowLeft size={28} strokeWidth={2.5} /></button>
        <h1 className="text-2xl font-black text-white">Admin</h1>
        <button data-testid="admin-logout-btn" onClick={logout} className="text-brand"><LogOut size={24} /></button>
      </header>

      <div className="px-4 pt-4 space-y-4">
        <div className="bg-black rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-onsurface3 text-xs font-black tracking-widest">EVENTO SELEZIONATO</p>
            <p className="text-white font-black">{event?.name} · {event ? formatDateShort(event.date_iso) : ""}</p>
          </div>
          <button data-testid="admin-swap-btn" onClick={() => setPicked(false)} className="text-brand"><ArrowLeftRight size={22} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button data-testid="admin-manage-games" onClick={() => nav("/admin/games")} className="bg-brand text-onbrand rounded-2xl py-4 font-black flex items-center justify-center gap-2">
            <Gamepad2 size={20} /> Games
          </button>
          <button data-testid="admin-manage-finals" onClick={() => nav("/admin/finals")} className="bg-brand text-onbrand rounded-2xl py-4 font-black flex items-center justify-center gap-2">
            <Trophy size={20} /> Finals
          </button>
        </div>

        <AthleteForm onCreated={loadRoster} />

        <div className="flex gap-2 bg-[#1c1c1e] p-1 rounded-full">
          {[["Men", "Uomo"], ["Women", "Donna"]].map(([v, l]) => (
            <button key={v} data-testid={`admin-gender-${v.toLowerCase()}`} onClick={() => setGender(v)}
              className={`flex-1 py-2.5 rounded-full font-bold ${gender === v ? "bg-brand text-onbrand" : "text-white"}`}>{l}</button>
          ))}
        </div>

        <SplitConfig gender={gender} />

        {groups.map(([div, label, bg]) => {
          const rows = (div === "*" ? athletes : athletes.filter((a) => a.division === div))
            .slice().sort((x, y) => (ranks[x.id] || 9999) - (ranks[y.id] || 9999));
          return (
            <div key={div}>
              <div className={`rounded-2xl px-5 py-3 flex items-center justify-between text-white ${bg}`}>
                <span className="text-xl font-black tracking-[0.15em]">{label}</span>
                <span className="text-sm font-bold opacity-90">{rows.length} atleti</span>
              </div>
              <div className="bg-surface2 rounded-2xl mt-2 overflow-hidden">
                {rows.length === 0 ? <p className="text-onsurface3 text-center py-5 text-sm">Nessun atleta</p> :
                  rows.map((a) => <RosterRow key={`${a.id}-${ranks[a.id] || 0}-${a.workout_position || 0}`} a={a} rank={ranks[a.id]} onChanged={loadRoster} />)}
              </div>
            </div>
          );
        })}
      </div>
      <BottomTab />
    </div>
  );
}
